import { useEffect, useMemo, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Settings, Save, AlertCircle } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';
import { GhlBackfillCard } from './GhlBackfillCard';

interface AdminSettings {
  beta_testflight_url: string | null;
  beta_play_url: string | null;
}

type SettingsKey = keyof AdminSettings;

/**
 * Light client-side hint only — the backend tolerates any string, and an
 * empty value is allowed (it disables a platform). We never hard-block save
 * on this; it just surfaces an inline nudge for obviously-malformed input.
 */
function looksLikeUrl(v: string): boolean {
  const trimmed = v.trim();
  if (trimmed === '') return true;
  return /^https?:\/\/.+/i.test(trimmed);
}

export function SettingsPage() {
  const apiUrl = useApiUrl();
  const { hasPermission, loaded } = useAdminPermissions();
  const canWrite = hasPermission('settings.write');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Server snapshot (last known persisted values) vs. the editable draft.
  const [original, setOriginal] = useState<AdminSettings>({
    beta_testflight_url: null,
    beta_play_url: null,
  });
  const [testflight, setTestflight] = useState('');
  const [play, setPlay] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminFetch(`${apiUrl}/v1/admin/settings`);
        const data: AdminSettings = await res.json();
        if (cancelled) return;
        setOriginal(data);
        setTestflight(data.beta_testflight_url ?? '');
        setPlay(data.beta_play_url ?? '');
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  // Build the request body — only the keys that actually changed vs. the
  // server snapshot. Empty string is sent as '' (intentionally clears a link).
  const changedBody = useMemo(() => {
    const body: Partial<AdminSettings> = {};
    const tf = testflight.trim();
    const pl = play.trim();
    if (tf !== (original.beta_testflight_url ?? '')) body.beta_testflight_url = tf;
    if (pl !== (original.beta_play_url ?? '')) body.beta_play_url = pl;
    return body;
  }, [testflight, play, original]);

  const hasChanges = Object.keys(changedBody).length > 0;
  const testflightInvalid = !looksLikeUrl(testflight);
  const playInvalid = !looksLikeUrl(play);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWrite || !hasChanges || saving) return;
    setSaving(true);
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/settings`, {
        method: 'PUT',
        body: JSON.stringify(changedBody),
      });
      // Refresh local snapshot from the server-confirmed state.
      const updated = (await res.json()) as { updated?: SettingsKey[] };
      const next: AdminSettings = {
        beta_testflight_url:
          'beta_testflight_url' in changedBody
            ? changedBody.beta_testflight_url || null
            : original.beta_testflight_url,
        beta_play_url:
          'beta_play_url' in changedBody
            ? changedBody.beta_play_url || null
            : original.beta_play_url,
      };
      setOriginal(next);
      setTestflight(next.beta_testflight_url ?? '');
      setPlay(next.beta_play_url ?? '');
      const count = updated?.updated?.length ?? Object.keys(changedBody).length;
      toast({
        title: 'Beta links updated',
        description: count === 1 ? '1 link saved.' : `${count} links saved.`,
      });
    } catch (e: any) {
      toast({
        title: 'Save failed',
        description: e?.message || 'Could not update beta links.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Settings</h2>
        <p className="text-muted-foreground">Platform configuration and preferences</p>
      </div>

      <form onSubmit={onSave} className="rounded-lg border bg-card">
        <div className="flex items-start gap-3 border-b px-6 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Settings className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Beta store-download links</h3>
            <p className="text-sm text-muted-foreground">
              These are the install links emailed to approved beta testers. Leave a field empty to
              disable that platform.
            </p>
          </div>
        </div>

        <div className="space-y-5 p-6">
          {loadError ? (
            <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {loadError}
            </div>
          ) : (
            <>
              {!canWrite && loaded && (
                <div className="rounded-md border border-yellow-400 bg-yellow-50 p-3 text-sm text-yellow-900">
                  You have read-only access. Contact a Super Admin if you need the{' '}
                  <code>settings.write</code> permission to edit these links.
                </div>
              )}

              <div>
                <label htmlFor="testflight-url" className="mb-1 block text-sm font-medium">
                  iOS TestFlight URL
                </label>
                <input
                  id="testflight-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://testflight.apple.com/join/…"
                  value={testflight}
                  onChange={(e) => setTestflight(e.target.value)}
                  disabled={loading || saving || !canWrite}
                  className="block w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
                {testflightInvalid && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-yellow-700">
                    <AlertCircle className="h-3 w-3" />
                    This doesn't look like an http(s) URL.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="play-url" className="mb-1 block text-sm font-medium">
                  Android Play Store URL
                </label>
                <input
                  id="play-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://play.google.com/store/apps/details?id=…"
                  value={play}
                  onChange={(e) => setPlay(e.target.value)}
                  disabled={loading || saving || !canWrite}
                  className="block w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
                {playInvalid && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-yellow-700">
                    <AlertCircle className="h-3 w-3" />
                    This doesn't look like an http(s) URL.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {canWrite && !loadError && (
          <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
            <button
              type="submit"
              disabled={loading || saving || !hasChanges}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </form>

      <GhlBackfillCard />
    </div>
  );
}
